import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ENVIRONMENT } from 'src/app/constants';
import {
  ICidade,
  IContrato,
  IEstado,
  IGetContratosQueryParams,
  ISite
} from 'src/app/interfaces';
import { IToken } from 'src/app/interfaces/token/IToken';
import { ListaPaginada } from 'src/app/models';
import {
  AuthenticationService,
  ContratoService,
  IBGEService,
  SiteService,
  UsuarioService
} from 'src/app/services';
import { CepService } from 'src/app/services/cep/cep.service';
import { SuprimentosService } from 'src/app/services/suprimentos/suprimentos.service';
import { Environment } from 'src/app/types';
import { aoMenosUmPreenchidoValidator } from 'src/app/validators';
import { ModalGeneralComponent } from 'src/app/components/modal-general/modal-general.component';

@Component({
  selector: 'app-novo-suprimento-km-do-brasil-a',
  templateUrl: './novo-suprimento-km-do-brasil-a.component.html',
  styleUrls: ['./novo-suprimento-km-do-brasil-a.component.scss']
})
export class NovoSuprimentoKmDoBrasilAComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild(ModalGeneralComponent) modal: ModalGeneralComponent;
  subscriptions$ = new Subscription();
  form: FormGroup;
  public get produtos(): FormArray {
    return this.form.get('produtos') as FormArray;
  }
  estados: IEstado[];
  cidades: ICidade[];
  contratos = new ListaPaginada<IContrato>();
  sites = new ListaPaginada<ISite>();
  token: IToken;
  exibeModal: boolean = true;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private ibgeService: IBGEService,
    private cepService: CepService,
    private suprimentosService: SuprimentosService,
    private siteService: SiteService,
    private contratoService: ContratoService,
    private usuarioService: UsuarioService,
    private authenticationService: AuthenticationService,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {}
  //#region Controle input contrato
  filtrarContratos(
    valor: IContrato | string,
    dados: IContrato[],
    exibir: this['exibirContrato']
  ): IContrato[] {
    return dados.filter((v) => {
      if (typeof valor === 'string') {
        return exibir(v).toLowerCase().includes(valor.toLowerCase());
      }

      return v.codContrato === valor?.codContrato;
    });
  }

  exibirContrato(valor: IContrato) {
    if (valor !== null && Object.keys(valor).length > 0) {
      return `${valor?.codContrato} - ${
        valor?.nomeFantasiaCliente || valor?.nomeCliente
      }`;
    }
    return '';
  }

  valorContrato(valor: IContrato) {
    return valor;
  }

  comparaContrato(valor1: string | IContrato | null, valor2: IContrato) {
    if (!valor1 || typeof valor1 === 'string') {
      return false;
    }

    if (valor1.codContrato !== valor2.codContrato) {
      return false;
    }

    return true;
  }
  //#endregion

  //#region Controle input site
  filtrarSites(
    valor: ISite | string,
    dados: ISite[],
    exibir: this['exibirSite']
  ): ISite[] {
    return dados.filter((v) => {
      if (typeof valor === 'string') {
        return exibir(v).toLowerCase().includes(valor.toLowerCase());
      }

      return v.codSite === valor?.codSite;
    });
  }

  exibirSite(valor: ISite) {
    if (valor !== null && Object.keys(valor).length > 0) {
      return `${valor?.nome} - ${valor?.endereco}`;
    }
    return '';
  }

  valorSite(valor: ISite) {
    return valor;
  }

  comparaSite(valor1: string | ISite | null, valor2: ISite) {
    if (!valor1 || typeof valor1 === 'string') {
      return false;
    }

    if (valor1?.codSite !== valor2?.codSite) {
      return false;
    }

    return true;
  }
  //#endregion
  getEstados() {
    this.subscriptions$.add(
      this.ibgeService.getEstados().subscribe({
        next: (value) => {
          this.estados = value;
        }
      })
    );
  }

  getCidades(estado: string) {
    this.subscriptions$.add(
      this.ibgeService.getCidadesPorEstado(estado).subscribe({
        next: (value) => {
          this.cidades = value;
        }
      })
    );
  }

  getEndereco(cep: string) {
    this.subscriptions$.add(
      this.cepService.getAddresByCep(cep).subscribe({
        next: ({ logradouro, bairro, complemento, uf: estado, localidade }) => {
          this.form.patchValue({
            logradouro,
            complemento,
            bairro,
            cidade: localidade.toUpperCase(),
            estado
          });
        }
      })
    );
  }

  getContratos() {
    const queryParams: IGetContratosQueryParams = {
      idUsuario: this.token.id,
      codContrato: this.token.codContrato,
      codGrupoEconomico: this.token.codGrupoEconomico,
      contratoAtivo: true,
      paginar: false
    };
    this.subscriptions$.add(
      this.contratoService.getContratos(queryParams).subscribe({
        next: ({ data }) => {
          const contratosComAcessorios = data.registros.filter(
            (el) => el.temAcessorios
          );
          data.registros = contratosComAcessorios;
          this.contratos = data;
        }
      })
    );
  }

  getUsuario() {
    this.subscriptions$.add(
      this.usuarioService
        .getUsuario({ idUsuario: this.token.id, usuarioId: this.token.id })
        .subscribe({
          next: ({
            data: {
              registros: [{ email, nome, telefone, celular }]
            }
          }) => {
            this.form.patchValue({
              email,
              nome,
              telefone,
              celular
            });
          }
        })
    );
  }

  getSite(codigo: string) {
    this.subscriptions$.add(
      this.siteService
        .getSite({
          codContrato: [codigo],
          codGrupoEconomico: this.token.codGrupoEconomico,
          tipoUsuario: this.token.tipoUsuario,
          paginar: false
        })
        .subscribe({
          next: ({ data }) => {
            this.sites = data;
          }
        })
    );
  }

  getAcessorios(codSite: string) {
    this.subscriptions$.add(
      this.suprimentosService
        .getAcessorios({
          idUsuario: this.token.id,
          codContrato: this.form.get('codContrato')?.value,
          codSite,
          somenteOriginais: true,
          somenteEquipamentosAtivos: true,
          paginar: false
        })
        .subscribe({
          next: ({ data: { registros } }) => {
            this.produtos.clear();
            for (let {
              codProdutoSuprimento,
              nomeProdutoSuprimento,
              nomeMarcaProdutoSuprimento,
              referenciaProdutoSuprimento,
              autonomiaSuprimento,
              tipoSuprimento
            } of registros) {
              const formGroup = this.fb.group({
                codProdutoSuprimento: [codProdutoSuprimento],
                referenciaProdutoSuprimento: [referenciaProdutoSuprimento],
                nomeProdutoSuprimento: [nomeProdutoSuprimento],
                nomeMarcaProdutoSuprimento: [nomeMarcaProdutoSuprimento],
                autonomiaSuprimento: [autonomiaSuprimento],
                tipoSuprimento: [tipoSuprimento],
                quantidade: [0, Validators.required],
                estoque: [0, Validators.required],
                vazios: [0, Validators.required]
              });
              this.produtos.push(formGroup);
            }
          }
        })
    );
  }

  prosseguir() {
    const produtos: any[] = [];
    const valor = this.form.getRawValue();
    for (let item of valor.produtos) {
      produtos.push(item);
    }

    this.subscriptions$.add(
      this.suprimentosService
        .postSuprimento({
          ...valor,
          codSite: this.form.value.site.codSite,
          empresa: this.environment.company,
          produtos
        })
        .subscribe({
          next: ({ data: { codigo } }) => {
            this.router.navigate(['/novo-suprimento/abrir-chamado/4', codigo]);
          }
        })
    );

    this.abrirModal();
  }

  inicializarFormulario() {
    this.form = this.fb.group(
      {
        idUsuario: [this.token.id, Validators.required],
        codContrato: ['', Validators.required],
        contrato: [null, Validators.required],
        site: [null, Validators.required],
        obs: ['', Validators.maxLength(3000)],
        email: [
          '',
          [Validators.required, Validators.email, Validators.maxLength(200)]
        ],
        telefone: [''],
        telefoneAlternativo: [''],
        celular: [''],
        nome: ['', Validators.required],
        logradouro: ['', [Validators.required, Validators.maxLength(60)]],
        numero: ['', Validators.required],
        complemento: ['', Validators.maxLength(20)],
        bairro: ['', [Validators.required, Validators.maxLength(30)]],
        cidade: ['', [Validators.required, Validators.maxLength(30)]],
        estado: ['', [Validators.required, Validators.maxLength(2)]],
        cep: ['', Validators.required],
        produtos: this.fb.array([], Validators.required),
        preencherEndereco: [false]
      },
      {
        validators: [
          aoMenosUmPreenchidoValidator(
            'telefone',
            'telefoneAlternativo',
            'celular'
          ),
          (control: AbstractControl) => {
            const itensFormArray = control.get('produtos') as FormArray;
            for (let control of itensFormArray.controls) {
              const quantidade = control.get('quantidade')?.value;
              if (quantidade) {
                return null;
              }
            }
            return { obrigatorioItens: true };
          }
        ]
      }
    );

    this.subscriptions$.add(
      this.form.get('contrato')?.valueChanges.subscribe({
        next: (contrato) => {
          if (!contrato || typeof contrato === 'string') {
            this.form.patchValue({
              codContrato: '',
              site: null
            });
            return;
          }

          this.produtos.clear();
          this.form.patchValue({
            codContrato: contrato.codContrato,
            site: null,
            obs: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: '',
            preencherEndereco: false
          });
        }
      })
    );

    this.subscriptions$.add(
      this.form.get('codContrato')?.valueChanges.subscribe({
        next: (value) => {
          if (value) {
            this.getSite(value);
          }
        }
      })
    );

    this.subscriptions$.add(
      this.form.get('site')?.valueChanges.subscribe({
        next: (value: ISite | string) => {
          if (value) {
            if (!value || typeof value === 'string') {
              return;
            }

            this.getAcessorios(value.codSite);
            this.form.patchValue({ preencherEndereco: true });
          }
        }
      })
    );

    this.subscriptions$.add(
      this.form.get('estado')?.valueChanges.subscribe({
        next: (value) => {
          if (value) {
            this.getCidades(value);
          }
        }
      })
    );

    this.subscriptions$.add(
      this.form.get('cep')?.valueChanges.subscribe({
        next: (value) => {
          if (
            value.length === 8 &&
            !this.form.get('preencherEndereco')?.value
          ) {
            this.getEndereco(value);
          }
        }
      })
    );

    this.subscriptions$.add(
      this.form.get('preencherEndereco')?.valueChanges.subscribe({
        next: (value) => {
          if (value) {
            const siteSelecionado: ISite = this.form.get('site')?.value;
            this.form.patchValue({
              logradouro: siteSelecionado.logradouro,
              numero: siteSelecionado.numero,
              complemento: siteSelecionado.complemento,
              bairro: siteSelecionado.bairro,
              cidade: siteSelecionado.cidade.toUpperCase(),
              estado: siteSelecionado.estado,
              cep: siteSelecionado.cep
            });

            this.form.get('logradouro')?.disable();
            this.form.get('numero')?.disable();
            this.form.get('complemento')?.disable();
            this.form.get('bairro')?.disable();
            this.form.get('cidade')?.disable();
            this.form.get('estado')?.disable();
            this.form.get('cep')?.disable();
            return;
          }

          this.form.get('logradouro')?.enable();
          this.form.get('numero')?.enable();
          this.form.get('complemento')?.enable();
          this.form.get('bairro')?.enable();
          this.form.get('cidade')?.enable();
          this.form.get('estado')?.enable();
          this.form.get('cep')?.enable();
        }
      })
    );
  }

  limparEndereco() {
    if (this.form.value['preencherEndereco']) {
      return;
    }

    this.form.patchValue({
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    });
  }

  abrirModal() {
    this.modal.openModal();
  }

  ngAfterViewInit() {
    if (this.exibeModal) {
      this.abrirModal();
      this.exibeModal = false;
    }
  }

  ngOnInit(): void {
    this.token = this.authenticationService.obterToken();
    this.inicializarFormulario();
    this.getContratos();
    this.getUsuario();
    this.getEstados();
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
